// App.js
import usePageTracking from './analytics/usePageTracking';
import React, { Component, Suspense } from 'react';
import './app.scss';
import FFHeader from './components/FFHeader';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Content, Theme, HeaderContainer } from '@carbon/react';
import 'core-js/modules/es.array.includes';
import 'core-js/modules/es.array.fill';
import 'core-js/modules/es.string.includes';
import 'core-js/modules/es.string.trim';
import 'core-js/modules/es.object.values';
import LandingPage from './content/LandingPage';
import RepoPage from './content/RepoPage';
import LoginPage from './content/LoginPage';
import ResetPasswordPage from './content/ResetPasswordPage/ResetPasswordPage';
import CreateAccountPage from './content/CreateAccountPage';
import BillingPage from './content/BillingPage';
import UsagePage from './content/UsagePage';
import SettingsPage from './content/SettingsPage';
import PrivacyPage from './content/PrivacyPage';
import AIPostsPage from './content/AIPosts/AIPostsPage';
import AIPostsTopicPage from './content/AIPosts/AIPostsTopicPage';
import AIPostsViewPage from './content/AIPosts/AIPostsViewPage';
import EmailIntegrationPage from './content/integrations/EmailIntegrationPage';
import SmsIntegrationPage from './content/integrations/SmsIntegrationPage';
import EvernoteIntegrationPage from './content/integrations/EvernoteIntegrationPage';
import GoogleDriveIntegrationPage from './content/integrations/GoogleDriveIntegrationPage/GoogleDriveIntegrationPage';
import GitHubIntegrationPage from './content/integrations/GitHubIntegrationPage/GitHubIntegrationPage';
// TODO: X/Twitter integration temporarily hidden — Twitter API changed, pending reimplementation
// import TwitterIntegrationPage from './content/integrations/TwitterIntegrationPage/TwitterIntegrationPage';
import S3IntegrationPage from './content/integrations/S3IntegrationPage/S3IntegrationPage';
import CustomApiIntegrationPage from './content/integrations/CustomApiIntegrationPage';
import OpenClawIntegrationPage from './content/integrations/OpenClawIntegrationPage';
import A2AIntegrationPage from './content/integrations/A2AIntegrationPage';
import StripeWrapper from './components/StripeWrapper';

import { GoogleOAuthProvider } from '@react-oauth/google';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import amplitude from './analytics/amplitude';
import { Identify } from '@amplitude/analytics-browser';

class App extends Component {
  constructor(props) {
    super(props);
    // Initialize state from localStorage
    const token = localStorage.getItem('token');
    const tokenTimestamp = localStorage.getItem('tokenTimestamp');
    const user = localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user'))
      : null;
    const email = localStorage.getItem('email');

    let isTokenValid = true;

    // Check if the token has expired
    if (token && tokenTimestamp) {
      const tokenAge = Date.now() - parseInt(tokenTimestamp, 10);
      const maxAge = (24 * 60 - 10) * 60 * 1000; // 24 hours minus 10 minutes in milliseconds
      if (tokenAge > maxAge) {
        // Token has expired
        isTokenValid = false;
        // Remove expired token and related data
        localStorage.removeItem('token');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('user');
        localStorage.removeItem('email');
      }
    }

    this.state = {
      token: isTokenValid ? token : null,
      user: isTokenValid ? user : null,
      email: isTokenValid ? email : null,
    };
  }

  componentDidMount() {
    // Check token expiration when the component mounts
    this.checkTokenExpiration();
    // Check token expiration every 10 minutes
    this.tokenCheckInterval = setInterval(
      this.checkTokenExpiration,
      10 * 60 * 1000
    ); // every 10 minutes
    // Listen for auth failures from authFetch wrapper (401/403 from any tab)
    window.addEventListener('ffai-auth-failed', this.handleAuthFailure);
  }

  componentWillUnmount() {
    // Clear the interval when the component unmounts
    clearInterval(this.tokenCheckInterval);
    window.removeEventListener('ffai-auth-failed', this.handleAuthFailure);
  }

  componentDidUpdate(prevProps, prevState) {
    // Save to localStorage when state changes
    if (prevState.token !== this.state.token) {
      if (this.state.token) {
        localStorage.setItem('token', this.state.token);
        // Save the current timestamp when the token is set
        localStorage.setItem('tokenTimestamp', Date.now().toString());
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('tokenTimestamp');
      }
    }
    if (prevState.user !== this.state.user) {
      if (this.state.user) {
        localStorage.setItem('user', JSON.stringify(this.state.user));
      } else {
        localStorage.removeItem('user');
      }
    }
    if (prevState.email !== this.state.email) {
      if (this.state.email) {
        localStorage.setItem('email', this.state.email);
      } else {
        localStorage.removeItem('email');
      }
    }
  }

  setGoogleAuthToken = googleToken => {
    console.log('Setting Google auth token:', googleToken);
    // Optionally, you can store it in state if needed:
    this.setState({ googleAuthToken: googleToken });
  };

  // Method to check if the token has expired
  checkTokenExpiration = () => {
    const tokenTimestamp = localStorage.getItem('tokenTimestamp');
    if (this.state.token && tokenTimestamp) {
      const tokenAge = Date.now() - parseInt(tokenTimestamp, 10);
      const maxAge = (24 * 60 - 10) * 60 * 1000; // 24 hours minus 10 minutes in milliseconds
      if (tokenAge > maxAge) {
        // Token has expired, log out the user
        this.handleLogout();
      }
    }
  };

  clearAuthState = () => {
    this.setState({ token: null, user: null, email: null });
    amplitude.reset();
    localStorage.removeItem('token');
    localStorage.removeItem('tokenTimestamp');
    localStorage.removeItem('user');
    localStorage.removeItem('email');
  };

  handleLogout = () => {
    this.clearAuthState();
    // Redirect to the landing page or perform other logout tasks
    this.props.navigate('/');
  };

  handleAuthFailure = () => {
    if (!this.state.token) return; // already logged out, ignore
    toast.error('Your session has expired. Please log in again.');
    this.clearAuthState();
    this.props.navigate('/login');
  };

  setToken = (newToken, newUser, newEmail) => {
    console.log(
      'Setting FF token to ' +
        newToken +
        ' email: ' +
        newEmail +
        ' user: ' +
        newUser
    );
    if (newUser) {
      amplitude.setUserId(newUser);

      const ident = new Identify().set('email', newEmail).set('name', newUser);
      amplitude.identify(ident);

      amplitude.track('Login', { method: 'password' });
    }
    this.setState({ token: newToken, user: newUser, email: newEmail });
    this.props.navigate('/');
  };
  //Use g100 instead of white if you want a dark theme
  render() {
    const { token, user, email } = this.state;
    return (
      <GoogleOAuthProvider clientId="929826295274-ntied11tv7jp00uggnm9ju25rt14hc1l.apps.googleusercontent.com">
        <Theme theme="white">
          <HeaderContainer
            render={({ isSideNavExpanded, onClickSideNavExpand }) => (
              <>
                <FFHeader
                  user={user}
                  email={email}
                  isLoggedIn={!!token}
                  onLogout={this.handleLogout}
                  isSideNavExpanded={isSideNavExpanded}
                  onClickSideNavExpand={onClickSideNavExpand}
                />
                <Content>
                  <Routes>
                    <Route
                      exact
                      path="/"
                      element={<LandingPage token={token} user={user} />}
                    />
                    <Route path="/posts" element={<AIPostsPage />} />
                    <Route
                      path="/posts/:topic"
                      element={<AIPostsTopicPage />}
                    />
                    <Route
                      path="/posts/:topic/:date/:slug"
                      element={<AIPostsViewPage />}
                    />
                    {token ? (
                      <>
                        <Route path="/repos" element={<RepoPage />} />
                        <Route
                          path="/billing"
                          element={
                            <Suspense
                              fallback={<div>Loading payment system...</div>}>
                              <StripeWrapper>
                                <BillingPage token={token} user={user} />
                              </StripeWrapper>
                            </Suspense>
                          }
                        />
                        <Route
                          path="/usage"
                          element={<UsagePage token={token} />}
                        />
                        <Route
                          path="/settings"
                          element={<SettingsPage token={token} user={user} />}
                        />
                        <Route
                          path="/privacy"
                          element={<PrivacyPage token={token} user={user} />}
                        />
                        <Route
                          path="/integration/email"
                          element={<EmailIntegrationPage />}
                        />
                        <Route
                          path="/integration/sms"
                          element={
                            <SmsIntegrationPage token={token} user={user} />
                          }
                        />
                        <Route
                          path="/integration/evernote"
                          element={
                            <EvernoteIntegrationPage
                              token={token}
                              user={user}
                            />
                          }
                        />
                        <Route
                          path="/integration/google-drive"
                          element={
                            <GoogleDriveIntegrationPage
                              token={token}
                              user={user}
                            />
                          }
                        />
                        <Route
                          path="/integration/github"
                          element={
                            <GitHubIntegrationPage token={token} user={user} />
                          }
                        />
                        <Route
                          path="/integration/s3"
                          element={
                            <S3IntegrationPage token={token} user={user} />
                          }
                        />
                        {/* TODO: X/Twitter route temporarily hidden — Twitter API changed, pending reimplementation */}
                        <Route
                          path="/integration/custom-api"
                          element={<CustomApiIntegrationPage token={token} />}
                        />
                        <Route
                          path="/integration/openclaw"
                          element={<OpenClawIntegrationPage token={token} />}
                        />
                        <Route
                          path="/integration/a2a"
                          element={<A2AIntegrationPage token={token} />}
                        />
                      </>
                    ) : (
                      <>
                        <Route
                          path="/createaccount"
                          element={
                            <Suspense
                              fallback={<div>Loading payment system...</div>}>
                              <StripeWrapper>
                                <CreateAccountPage
                                  token={token}
                                  setToken={this.setToken}
                                />
                              </StripeWrapper>
                            </Suspense>
                          }
                        />
                      </>
                    )}
                    <Route
                      path="/login"
                      element={
                        <LoginPage
                          setToken={this.setToken}
                          setGoogleAuthToken={this.setGoogleAuthToken}
                        />
                      }
                    />
                    <Route
                      path="/resetpassword"
                      element={<ResetPasswordPage />}
                    />
                  </Routes>
                </Content>
              </>
            )}
          />
        </Theme>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover={false}
          theme="colored"
        />
      </GoogleOAuthProvider>
    );
  }
}

// Create a functional component wrapper
const AppWrapper = props => {
  usePageTracking();
  const navigate = useNavigate();
  return <App {...props} navigate={navigate} />;
};

export default AppWrapper;
